import { APIUrl } from './apiConfig';

/**
 * Calls to the school endpoints that are not plain react-admin CRUD -- the
 * test builder, assigning, results. One place for the token, the JSON and
 * the error sentence.
 *
 * The API answers `{status, message, result}`. Anything but `success` is
 * thrown as an Error carrying the API's own message, which is written for a
 * teacher to read ("The window must end after it starts.") and is shown as is.
 */

/** An Error that also carries whatever the failing response sent with it. */
export interface ApiError extends Error {
    status?: number;
    result?: any;
}

export type ApiResponse<T = any> = { status: string; message: string; result: T; total?: number; [k: string]: any };

const headers = () => ({
    'Content-Type': 'application/json',
    Authorization: `Bearer ${localStorage.getItem('jwt_access_token')}`,
});

const url = (path: string, query?: Record<string, any>) => {
    const q = new URLSearchParams();
    Object.entries(query || {}).forEach(([k, v]) => {
        if (v !== undefined && v !== null && v !== '') q.append(k, String(v));
    });
    const qs = q.toString();
    return `${APIUrl}${path}${qs ? `?${qs}` : ''}`;
};

async function call<T>(method: string, path: string, body?: any, query?: Record<string, any>): Promise<ApiResponse<T>> {
    const res = await fetch(url(path, query), {
        method,
        headers: headers(),
        body: body === undefined ? undefined : JSON.stringify(body),
    });
    let json: any = null;
    try {
        json = await res.json();
    } catch {
        throw new Error(`The server answered ${res.status} with no readable message.`);
    }
    if (!json || json.status !== 'success') {
        const err: ApiError = new Error((json && json.message) || `The server answered ${res.status}.`);
        // Some endpoints send a body WITH their failure -- the migration runner
        // returns the log of everything it managed before it stopped, which is
        // the most useful thing an operator can be handed. Carry it along; the
        // message alone stays the default for callers that do not look.
        err.status = res.status;
        err.result = json ? json.result : undefined;
        throw err;
    }
    return json;
}

export const schoolApi = {
    get: <T = any>(path: string, query?: Record<string, any>) => call<T>('GET', path, undefined, query),
    post: <T = any>(path: string, body: any = {}) => call<T>('POST', path, body),
    put: <T = any>(path: string, body: any) => call<T>('PUT', path, body),
    del: <T = any>(path: string) => call<T>('DELETE', path),
};

// ---------------------------------------------------------------------------
// Time. The API takes ISO 8601 and returns ISO UTC ("...Z"). The panel shows
// and edits in the browser's own zone; conversion happens only here.
// ---------------------------------------------------------------------------

/** "2026-09-12T04:30:00Z" -> "12 Sep 2026, 10:00" in the browser's zone. */
export const showTime = (iso?: string | null) => {
    if (!iso) return '—';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleString(undefined, { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

/** ISO -> the value a <input type="datetime-local"> wants, in the browser's zone. */
export const toLocalInput = (iso?: string | null) => {
    if (!iso) return '';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '';
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

/** A datetime-local value (browser's zone) -> ISO UTC for the API. */
export const fromLocalInput = (v?: string | null) => {
    if (!v) return null;
    const d = new Date(v);
    return Number.isNaN(d.getTime()) ? null : d.toISOString();
};

/** "2" -> "B" for a four-option question; an integer answer is shown as is. */
export const answerLabel = (answer: string | null | undefined, type?: string) => {
    if (answer === null || answer === undefined || answer === '') return '—';
    if (type === 'integer') return answer;
    return answer
        .split(',')
        .map(a => {
            const n = parseInt(a.trim(), 10);
            return n >= 1 && n <= 4 ? 'ABCD'[n - 1] : a.trim();
        })
        .join(', ');
};

export const minutes = (seconds?: number | null) => {
    if (seconds === null || seconds === undefined) return '—';
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return m > 0 ? `${m}m ${String(s).padStart(2, '0')}s` : `${s}s`;
};
